import React, { useState } from 'react';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { toast } from 'react-toastify';
import EnumsService from '../../../Services/enums.service';
import { syncAccountCardToDocumentEnums } from '../../../Utils/cacheManager';
import {
  getCardBankDetails,
  isBankPaymentType,
  rowHasBankDetails,
  rowMatchesCardBankDetails,
  rowToCardBankDetailsPayload,
  withCardBankDetails,
} from '../../../Utils/accountCardBankDetails';
import styles from './AccountCardBankDetailsBar.module.scss';

/**
 * Shows the selected account card's bank details above a list of payment rows and lets the
 * user copy them into check / bank-transfer rows, or save a row's bank details back to the card.
 *
 * `rows` are payment rows carrying `itemType` and `itemTypeIndex`.
 * `onFillRows(targets, details)` receives the rows to fill and the card details in row field names.
 * `onCardSaved(updatedCard)` receives the card with its new bank details.
 */
const AccountCardBankDetailsBar = ({
  t,
  direction,
  caseId,
  token,
  card,
  rows,
  onFillRows,
  onCardSaved,
  disabled = false,
}) => {
  const [saving, setSaving] = useState(false);

  const cardId = card?.cardId ?? card?.CardId;
  const bankRows = (rows || []).filter((row) => isBankPaymentType(row.itemType));
  if (!cardId || !caseId || bankRows.length === 0) return null;

  const cardDetails = getCardBankDetails(card);
  const rowsToFill = cardDetails ? bankRows.filter((row) => !rowHasBankDetails(row)) : [];
  const rowToSave = bankRows.find(
    (row) => rowHasBankDetails(row) && !rowMatchesCardBankDetails(row, cardDetails)
  );

  const summary = cardDetails
    ? [
        cardDetails.bankCode && cardDetails.bankName
          ? `${cardDetails.bankCode} - ${cardDetails.bankName}`
          : cardDetails.bankCode || cardDetails.bankName,
        cardDetails.bankBranchCode && `${t('BankBranchCode')} ${cardDetails.bankBranchCode}`,
        cardDetails.supplierAccountNumber && `${t('BankAccountNumber')} ${cardDetails.supplierAccountNumber}`,
      ]
        .filter(Boolean)
        .join(' · ')
    : t('NoBankDetailsOnCard');

  const handleSave = async () => {
    if (!rowToSave) return;
    setSaving(true);
    try {
      const res = await EnumsService.UpdateAccountCardBankDetails(
        caseId,
        cardId,
        rowToCardBankDetailsPayload(rowToSave),
        token
      );
      if (!res.ok) throw new Error(`UpdateBankDetails failed: ${res.status}`);
      const saved = await res.json();
      const updatedCard = withCardBankDetails(card, saved);
      syncAccountCardToDocumentEnums(caseId, { ...updatedCard, cardId: String(cardId) });
      onCardSaved?.(updatedCard);
      toast.success(t('BankDetailsSavedToCard'));
    } catch (error) {
      console.error('Failed to save bank details to the account card', error);
      toast.error(t('FailedToSaveBankDetails'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.bar} dir={direction}>
      <AccountBalanceIcon className={styles.icon} fontSize="small" />
      <span className={styles.label}>{t('CardBankDetails')}:</span>
      <span className={`${styles.summary} ${cardDetails ? '' : styles.summaryEmpty}`} title={summary}>
        {summary}
      </span>
      <div className={styles.actions}>
        {rowsToFill.length > 0 && (
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => onFillRows?.(rowsToFill, cardDetails)}
            disabled={disabled}
          >
            {t('FillBankDetailsFromCard')}
          </button>
        )}
        {rowToSave && (
          <button
            type="button"
            className={styles.actionBtn}
            onClick={handleSave}
            disabled={disabled || saving}
          >
            {cardDetails ? t('UpdateBankDetailsOnCard') : t('SaveBankDetailsToCard')}
          </button>
        )}
      </div>
    </div>
  );
};

export default AccountCardBankDetailsBar;
